import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import { CardEditProps } from "@/interfaces/Card";
import Course, { CourseForm } from "@/interfaces/Course";
import { useGetCourseQuery, useUpdateCourseMutation } from "@/services/courses";

import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FC, useEffect, useRef } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";

const CourseEditCard: FC<CardEditProps<Course>> = ({ id }) => {
  const { data, isLoading, isError, error } = useGetCourseQuery(id);
  const [updateCourseAsync] = useUpdateCourseMutation();
  const defaultAdditional = useRef<{ pageIndex: number }>({
    pageIndex: 0,
  }).current;
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<Partial<CourseForm>>();

  useEffect(() => {
    data &&
      reset({
        ...data,
      });
  }, [data]);

  const onSubmit: SubmitHandler<Partial<CourseForm>> = async ({
    ...newData
  }) => {
    const result = getDifferenceBetweenTwoObjects<Partial<CourseForm>>(
      data,
      newData
    );
    toast.promise(updateCourseAsync([id, result]).unwrap(), {
      pending: "Modification de la formation en cours ...",
      success: {
        render() {
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              La formation à été modifiée 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<Partial<CourseForm>> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <Card>
        <CardHeader title="Modification de la formation" />
        <Card.Body className="bg-light">
          <Row className="gx-3">
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nom <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : Formation de développement web"
                  type="text"
                  {...register("name", {
                    required: {
                      value: true,
                      message: INVALID_NAME_MESSAGE,
                    },
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            className="mt-3"
          >
            Confirmer
          </Button>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default CourseEditCard;
